import { gql } from '@apollo/client';
import React from 'react';
import I18n from 'i18n-js';
import classNames from 'classnames';

import ConditionDisplay from '@reverbdotcom/commons/src/components/condition_display';
import SanitizedRender from '@reverbdotcom/commons/src/components/sanitized_render';
import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import Item2ReturnPolicy, { Item2ReturnPolicyFragment } from './item2_return_policy';
import { listingItemPagePath } from '@reverbdotcom/commons/src/url_helpers';
import { Item2Image, Item2ImageFragment } from './item2_image';
import Item2Pricing, { Item2PricingFragment } from './item2_pricing';
import { FeaturedListingModuleData } from '../schemas/rql';
import { buildListingClickEvent } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import { FeaturedListingShopInfo, FeaturedListingShopInfoFragment } from './featured_listing_shop_info';

export const FeaturedListingModuleFragment = gql`
  fragment FeaturedListingModuleData on Listing {
    _id
    title
    slug
    description
    condition {
      displayName
      conditionSlug
    }
    ...Item2Image
    ...FeaturedListingShopInfoData
    ...Item2PricingData
    ...Item2ReturnPolicyData
  }
  ${Item2ImageFragment}
  ${FeaturedListingShopInfoFragment}
  ${Item2PricingFragment}
  ${Item2ReturnPolicyFragment}
`;

interface IProps {
  listing: FeaturedListingModuleData.Fragment;
  hideOtherPageContent?: boolean;
  isBumped?: boolean;
  isLoading?: boolean;
  trackName?: string;
  showBorder?: boolean;
}

export function FeaturedListingModule(props: IProps) {
  const [shouldHideListing, toggleHideListing] = React.useState<boolean>(false);
  const { listing, hideOtherPageContent, isBumped, isLoading, showBorder } = props;
  const classes = classNames(
    'featured-listing-module',
    { 'featured-listing-module--bottom-border-only': !showBorder },
    { 'featured-listing-module--hide-other-content': hideOtherPageContent },
    { 'featured-listing-module--hidden': hideOtherPageContent && shouldHideListing },
    { 'featured-listing-module--loading': !listing && isLoading },
    { 'featured-listing-module--reloading': listing && isLoading },
  );

  if (!listing && isLoading) {
    return (
      <section className={classes}>
        <article className="featured-listing-module__wrapper">
          <div className="featured-listing-module__image-wrapper">
            <div className="featured-listing-module__image"></div>
            <div className="featured-listing-module__image featured-listing-module__image--mobile">
              <li></li>
              <li></li>
              <li></li>
            </div>
          </div>
        </article>
      </section>
    );
  }

  if (!listing) { return null; }

  const { condition, title } = listing;

  return (
    <>
      <h3 className="featured-listing-module__heading">
        {I18n.t('discovery.csp2.featuredListing')}
      </h3>
      <section className={classes}>
        <article className="featured-listing-module__wrapper">
          <div className="featured-listing-module__image-wrapper">
            <div className="featured-listing-module__image">
              <Item2Image
                listing={listing}
              />
            </div>
            <div className="featured-listing-module__image featured-listing-module__image--mobile">
              <Item2Image
                listing={listing}
                hidePrimary
              />
            </div>
          </div>
          <div className="featured-listing-module__main">
            <CoreLink
              className="featured-listing-module__title"
              to={listingItemPagePath(listing)}
              clickEvent={buildListingClickEvent({
                listing: props.listing,
                componentName: props.trackName,
                displayStyle: 'title link',
              })}
            >
              {title}
            </CoreLink>
            <ConditionDisplay
              slug={condition?.conditionSlug}
              displayName={condition?.displayName}
              textOnly
              showTooltip
            />
            <div className="featured-listing-module__return-policy">
              <Item2ReturnPolicy listing={listing} oneLineDisplay />
            </div>
            <div className="featured-listing-module__details">
              <div className="featured-listing-module__primary">
                <div className="featured-listing-module__shop">
                  <FeaturedListingShopInfo listing={listing} />
                </div>
                <div className="featured-listing-module__description">
                  <div className="featured-listing-module__section-header">
                    {I18n.t('discovery.item2Description.about')}
                  </div>
                  <SanitizedRender
                    html={listing.description}
                    htmlTag="div"
                    className="item2-description__content"
                    truncationLimit={200}
                    inlineTagsOnly
                    readMoreUrl={listingItemPagePath(listing)}
                  />
                </div>
                {isBumped &&
                  <div className="featured-listing-module__bumptag">
                    {I18n.t('commons.bumpsView.header')}
                  </div>
                }
                <CoreLink
                  className="featured-listing-module__secondary__link--mobile-only"
                  to={listingItemPagePath(listing)}
                  clickEvent={buildListingClickEvent({
                    listing: props.listing,
                    componentName: props.trackName,
                    displayStyle: 'secondary link',
                  })}
                >
                  {I18n.t('discovery.featuredListingModule.viewListing')}
                </CoreLink>
              </div>
              <div className="featured-listing-module__secondary">
                <Item2Pricing
                  listing={listing}
                  negotiations={[]}
                  onFeaturedListing
                />
                {!props.hideOtherPageContent &&
                  <CoreLink
                    className="featured-listing-module__secondary__link"
                    to={listingItemPagePath(listing)}
                    clickEvent={buildListingClickEvent({
                      listing: props.listing,
                      componentName: props.trackName,
                      displayStyle: 'secondary link',
                    })}
                  >
                    {I18n.t('discovery.featuredListingModule.viewListing')}
                  </CoreLink>
                }
              </div>
            </div>
          </div>
        </article>
        {props.hideOtherPageContent &&
          <div className="featured-listing-module__toggle-wrapper">
            <button
              className="featured-listing-module__toggle-action"
              onClick={() => toggleHideListing(!shouldHideListing)}
              type="button"
            >
              {I18n.t('discovery.featuredListingModule.viewListing')}
            </button>
          </div>
        }
      </section>
    </>
  );
}

export default FeaturedListingModule;
